/**
 * Orange dashboard banner for one active site announcement.
 */

import { Icon } from '@components/Icon';
import type { SiteAnnouncement } from '@lambda/shared/types';
import { normalizeAnnouncementIcon } from '@constants/announcementIcons';

import './SiteAnnouncementBanner.css';

export interface SiteAnnouncementBannerProps {
  announcement: SiteAnnouncement;
  onDismiss?: () => void;
  /** `landing` = full-width strip at top of public site; default = dashboard card. */
  layout?: 'dashboard' | 'landing';
}

export function SiteAnnouncementBanner({
  announcement,
  onDismiss,
  layout = 'dashboard',
}: SiteAnnouncementBannerProps) {
  const iconType = normalizeAnnouncementIcon(announcement.iconType);
  const className =
    layout === 'landing'
      ? 'bhg-site-announcement-banner bhg-site-announcement-banner--landing'
      : 'bhg-site-announcement-banner';

  return (
    <div className={className} role="status" aria-live="polite">
      <div className="bhg-site-announcement-banner__inner">
        <span className="bhg-site-announcement-banner__icon" aria-hidden>
          <Icon
            type={iconType}
            colorVariant="orange"
            size="lg"
            style="outlined"
            backgroundShape="circle"
          />
        </span>
        <div className="bhg-site-announcement-banner__content">
          <p className="bhg-site-announcement-banner__title">{announcement.title}</p>
          <p className="bhg-site-announcement-banner__message">{announcement.message}</p>
        </div>
        {onDismiss ? (
          <button
            type="button"
            className="bhg-site-announcement-banner__dismiss"
            onClick={onDismiss}
            aria-label={`Dismiss announcement: ${announcement.title}`}
          >
            <Icon type="circle-x" size="md" style="outlined" colorVariant="gray" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
